// Composants partagés + API extension
import { useState, useEffect } from "react";
import api from "./api";

// ── DESIGN TOKENS ────────────────────────────────────────────────────────────
export const C = {
  navy:    "#1B2A4A", navy2:   "#243F72", navy3:   "#324E81", navylt:  "#E8EDF5",
  gold:    "#C8902C", golddm:  "#9A6E1E", goldlt:  "#F7EBCF",
  teal:    "#1F7A6C", teallt:  "#DBEEE9", tealdm:  "#155A50",
  purp:    "#3C3489", purplt:  "#EEEDFE",
  amber:   "#B45309", amberlt: "#FEF3C7",
  green:   "#166534", greenlt: "#DCFCE7",
  red:     "#991B1B", redlt:   "#FEE2E2",
  blue:    "#185FA5", bluelt:  "#E6F1FB",
  gray:    "#F7F3EA", gray2:   "#FBF9F4", border: "#E2DDD5",
  text:    "#1B2A4A", mid:     "#5F5E5A", muted:  "#999990",
};

const EUR_FMT = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
export const fmtEur = (n) => EUR_FMT.format(Number(n) || 0);
export const STATUS_COLOR = { Nouveau: [C.bluelt, C.blue], "Contacté": [C.amberlt, C.amber], "Bilan réalisé": [C.purplt, C.purp], "Proposition envoyée": [C.navylt, C.navy2], "Signée": [C.teallt, C.tealdm], Client: [C.greenlt, C.green], Perdu: [C.redlt, C.red], "Transfert en attente": [C.goldlt, C.golddm], "À relancer": [C.redlt, C.red], Converti: [C.greenlt, C.green], "RDV fixé": [C.teallt, C.teal], "En attente": [C.amberlt, C.amber], "Relance J+7": [C.bluelt, C.blue], Annulé: [C.redlt, C.red], Versé: [C.greenlt, C.green], "À verser": [C.amberlt, C.amber] };

// ── API extensions ────────────────────────────────────────────────────────────
export const prestationsApi = {
  list:   () => api.get("/prestations").then(r => r.data),
  create: (data) => api.post("/prestations", data).then(r => r.data),
  update: (id, data) => api.put(`/prestations/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/prestations/${id}`).then(r => r.data),
};

export const reglagesApi = {
  get:    () => api.get("/reglages").then(r => r.data),
  consultant: () => api.get("/reglages/consultant").then(r => r.data),
  update: (data) => api.put("/reglages", data).then(r => r.data),
  journalRib: () => api.get("/reglages/journal-rib").then(r => r.data),
};

export const parcoursApi = {
  list:   (statut) => api.get("/parcours", { params: statut ? { statut } : {} }).then(r => r.data),
  get:    (id) => api.get(`/parcours/${id}`).then(r => r.data),
  create: (data) => api.post("/parcours", data).then(r => r.data),
  patch:  (id, data) => api.patch(`/parcours/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/parcours/${id}`).then(r => r.data),
  foyer:  (email) => api.get(`/parcours/foyer/${encodeURIComponent(email)}`).then(r => r.data),
};

export const rdvApi = {
  list:   () => api.get("/rendez-vous").then(r => r.data),
  create: (data) => api.post("/rendez-vous", data).then(r => r.data),
  update: (id, data) => api.put(`/rendez-vous/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/rendez-vous/${id}`).then(r => r.data),
  demarrer: (id) => api.post(`/rendez-vous/${id}/demarrer`).then(r => r.data),
};

// ── Constantes Catalogue ────────────────────────────────────────────────────
export const PRESTATION_TYPES = [
  { value: "paiement_unique",      label: "Paiement unique" },
  { value: "abonnement_mensuel",   label: "Abonnement mensuel" },
  { value: "orientation_gratuite", label: "Orientation gratuite" },
  { value: "remise",               label: "Remise" },
];
export const PRESTATION_PUBLICS = [
  { value: "particulier", label: "Particulier" },
  { value: "dirigeant",   label: "Dirigeant" },
  { value: "tous",        label: "Tous" },
];
export const PRESTATION_BASES = [
  { value: "TTC", label: "TTC" },
  { value: "HT",  label: "HT" },
  { value: "NA",  label: "Non applicable" },
];

export const fdate = (iso) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};
export const fdatetime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? (iso || "—") : `${fdate(iso)} ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

// Saisie de date JJ/MM/AAAA (vide par défaut, valeur ISO AAAA-MM-JJ)
export const DateInput = ({ value, onChange, error, ...rest }) => {
  const toTxt = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v || "") ? `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : "");
  const [txt, setTxt] = useState(toTxt(value));
  const [bad, setBad] = useState(false);
  useEffect(() => { if (value && toTxt(value) !== txt) setTxt(toTxt(value)); if (!value && txt.length === 10 && !bad) setTxt(""); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const valide = (d) => { const iso = `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`; const x = new Date(`${iso}T00:00:00Z`); return !isNaN(x) && x.toISOString().slice(0, 10) === iso ? iso : null; };
  const change = (e) => {
    const d = e.target.value.replace(/\D/g, "").slice(0, 8);
    const t = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
    setTxt(t);
    const iso = d.length === 8 ? valide(d) : null;
    setBad(d.length === 8 && !iso);
    onChange(iso || "");
  };
  const msg = bad ? "Date invalide" : error;
  return (
    <div style={{ width: "100%" }}>
      <Input {...rest} value={txt} onChange={change} onBlur={() => setBad(txt.length > 0 && txt.length < 10 ? true : bad)} placeholder="JJ/MM/AAAA" inputMode="numeric" maxLength={10} style={{ ...(rest.style || {}), ...(msg ? { borderColor: C.red } : {}) }} />
      {msg && <div style={{ color: C.red, fontSize: 11, marginTop: 3, textTransform: "none" }} data-testid={`${rest["data-testid"] || "date"}-erreur`}>{msg}</div>}
    </div>
  );
};

export const downloadCsv = (filename, cols, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const txt = [cols.map(c => esc(c[1])).join(";"), ...rows.map(r => cols.map(c => esc(typeof c[0] === "function" ? c[0](r) : r[c[0]])).join(";"))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + txt], { type: "text/csv;charset=utf-8" }));
  a.download = filename; a.click();
};
export const authDownload = (path, filename, open) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } })
  .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); if (open) return window.open(u, "_blank"); const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); });

export const leadsV2Api = {
  meta: () => api.get("/leads/meta").then(r => r.data),
  list: () => api.get("/leads").then(r => r.data),
  get: (id) => api.get(`/leads/${id}`).then(r => r.data),
  create: (data) => api.post("/leads", data).then(r => r.data),
  update: (id, data) => api.put(`/leads/${id}`, data).then(r => r.data),
  statut: (id, data) => api.post(`/leads/${id}/statut`, data).then(r => r.data),
  note: (id, texte) => api.post(`/leads/${id}/notes`, { texte }).then(r => r.data),
  cibles: (id) => api.get(`/leads/${id}/transfert/cibles`).then(r => r.data),
  transferer: (id, data) => api.post(`/leads/${id}/transfert`, data).then(r => r.data),
  recherche: (q) => api.get("/leads/recherche", { params: { q } }).then(r => r.data),
  remove: (id) => api.delete(`/leads/${id}`).then(r => r.data),
  exportRgpd: (id) => api.get(`/admin/leads/${id}/export`).then(r => r.data),
  anonymiser: (id) => api.post(`/admin/leads/${id}/anonymiser`).then(r => r.data),
  dormants: () => api.get("/admin/leads/dormants").then(r => r.data),
  doublons: () => api.get("/admin/leads/doublons").then(r => r.data),
  trancher: (id, decision) => api.post(`/admin/leads/doublons/${id}`, { decision }).then(r => r.data),
  transferts: () => api.get("/admin/leads/transferts").then(r => r.data),
  deciderTransfert: (id, decision, commentaire) => api.post(`/admin/leads/transferts/${id}`, { decision, commentaire }).then(r => r.data),
  lierParcours: (pid, data) => api.post(`/parcours/${pid}/lead`, data).then(r => r.data),
};
export const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || (Array.isArray(d) ? d.map(x => x.msg).join(", ") : "Erreur"); };
export const LEAD_SOURCES = ["Recommandation d'un client", "Réseau personnel", "Salon ou conférence", "Réseaux sociaux", "Partenaire professionnel", "Site web ou code promo", "Lien personnel du consultant", "Autre"];
export const LEAD_BESOINS = ["Particulier", "Dirigeant", "Après un décès", "Foncier Clair"];
export const BESOIN_TYPE = { Particulier: "bilan_succession", Dirigeant: "bilan_dirigeants", "Après un décès": "ouvrir_succession", "Foncier Clair": "foncier_clair" };
export const leadStatutLabel = (l) => l?.transfert?.statut === "en_attente" ? "Transfert en attente" : (l?.statut_label || l?.statut);

export const leadsExtraApi = {
  get: (id) => api.get(`/leads/${id}`).then(r => r.data),
  update: (id, data) => api.put(`/leads/${id}`, data).then(r => r.data),
  logEnvoi: (id, data) => api.post(`/leads/${id}/envoi`, data).then(r => r.data),
  exportUrl: () => (process.env.REACT_APP_BACKEND_URL || "") + "/api/leads/export",
};

export const documentsApi = {
  list: () => api.get("/documents").then(r => r.data),
  upload: (file, nom, description) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("nom", nom);
    fd.append("description", description || "");
    return api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  update: (id, data) => api.put(`/documents/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/documents/${id}`).then(r => r.data),
  download: async (id, filename) => {
    const r = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "document";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export const fmtSize = (bytes) => {
  if (bytes < 1024) return bytes + " o";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " ko";
  return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
};

export const cotisationsApi = {
  list: () => api.get("/cotisations").then(r => r.data),
  stats: () => api.get("/cotisations/stats").then(r => r.data),
  create: (data) => api.post("/cotisations", data).then(r => r.data),
  update: (id, data) => api.put(`/cotisations/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/cotisations/${id}`).then(r => r.data),
};

export const FORMULE_PRIX = { Starter: 149, Conseiller: 249, Premium: 349 };

// ── UI Components ────────────────────────────────────────────────────────────
export const Badge = ({ text }) => {
  const colors = STATUS_COLOR[text] || [C.navylt, C.navy2];
  const palierColors = { Expert: [C.goldlt, C.golddm], Confirmé: [C.teallt, C.teal], Starter: [C.bluelt, C.blue] };
  const [bg, fg] = palierColors[text] || colors;
  return <span style={{ background: bg, color: fg, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center" }}>{text}</span>;
};

export const Btn = ({ children, onClick, variant = "ghost", small, disabled, type = "button", style = {}, ...rest }) => {
  const styles = {
    gold:  { background: C.gold,  color: C.navy, border: "none" },
    ghost: { background: "transparent", color: C.mid, border: `1px solid ${C.border}` },
    navy:  { background: C.navy2, color: "#fff",  border: "none" },
    red:   { background: C.redlt, color: C.red, border: `1px solid ${C.red}33` },
    teal:  { background: C.teal, color: "#fff", border: "none" },
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick} {...rest} style={{ ...styles[variant], borderRadius: 8, padding: small ? "5px 12px" : "8px 16px", fontSize: small ? 11 : 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? .5 : 1, transition: "opacity .15s", ...style }}>
      {children}
    </button>
  );
};

export const Input = (props) => (
  <input {...props} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%", background: "#fff", boxSizing: "border-box", ...(props.style || {}) }} />
);

export const Textarea = (props) => (
  <textarea {...props} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%", background: "#fff", boxSizing: "border-box", resize: "vertical", minHeight: 72, ...(props.style || {}) }} />
);

export const Select = ({ children, style, ...props }) => (
  <select {...props} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%", background: "#fff", cursor: "pointer", boxSizing: "border-box", ...style }}>
    {children}
  </select>
);

export const FormField = ({ label, required, children, error, compact }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: compact ? 3 : 5 }}>
    <label style={{ fontSize: compact ? 10 : 11, fontWeight: 600, color: error ? C.red : C.mid, textTransform: "uppercase", letterSpacing: ".05em" }}>
      {label} {required && <span style={{ color: C.red }}>*</span>}
    </label>
    {children}
    {error && <span style={{ fontSize: 10, color: C.red }}>{error}</span>}
  </div>
);

export const Alert = ({ type, children }) => {
  const colors = { info: [C.bluelt, C.blue], success: [C.greenlt, C.green], warn: [C.amberlt, C.amber], error: [C.redlt, C.red] };
  const [bg, border] = colors[type] || colors.info;
  return <div style={{ background: bg, borderLeft: `3px solid ${border}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: border, marginBottom: 16, display: "flex", gap: 10 }}>{children}</div>;
};

export const Modal = ({ title, onClose, children, footer, wide }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflowY: "auto" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{ background: "#fff", borderRadius: 16, width: wide ? 780 : 560, maxWidth: "100%", maxHeight: "calc(100vh - 40px)", overflowY: "auto", marginTop: 20, marginBottom: 20 }}>
      <div style={{ padding: "18px 24px", borderBottom: `.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.navy }}>{title}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: C.muted, lineHeight: 1 }}>×</button>
      </div>
      <div style={{ padding: 24 }}>{children}</div>
      {footer && <div style={{ padding: "14px 24px", borderTop: `.5px solid ${C.border}`, display: "flex", gap: 8, justifyContent: "flex-end", position: "sticky", bottom: 0, background: "#fff", zIndex: 1 }}>{footer}</div>}
    </div>
  </div>
);

export const Section = ({ title, defaultOpen = true, children, icon }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", background: C.gray2, border: "none", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontFamily: "inherit" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>{icon}{title}</span>
        <span style={{ fontSize: 16, color: C.muted, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▾</span>
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
};

export const Spinner = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
    <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.gold, borderRadius: "50%", animation: "auprea-spin 1s linear infinite" }} />
    <style>{`@keyframes auprea-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export const useAsync = (fn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const reload = () => {
    setLoading(true);
    fn().then(setData).catch(setErr).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, deps);
  return { data, loading, err, reload };
};

export const ageFromDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

export const ageBracket = (age) => {
  if (age == null) return null;
  if (age < 40) return "< 40 ans";
  if (age < 55) return "40-55 ans";
  if (age < 65) return "55-65 ans";
  if (age < 75) return "65-75 ans";
  return "75 ans et +";
};

export const CANAUX = ["Recommandation", "Réseaux sociaux", "Site web", "Salon/événement", "Autre"];
export const STATUTS_FAM = ["Marié(e)", "Célibataire", "Divorcé(e)", "Veuf(ve)", "En couple"];
export const BIENS = ["Résidence principale", "Autre bien immobilier", "Épargne", "PEL", "Assurance-vie", "Entreprise", "Autre"];
export const SIT_FAM = ["Enfants", "Petits-enfants", "Famille recomposée", "Proche dépendant", "Proches à l'étranger"];
export const NIVEAUX_ORG = ["Rien n'est organisé", "Quelques documents", "A déjà travaillé avec un notaire", "Déjà bien organisé"];
